// Scope-guard helpers for the hierarchy landing/redirect guard.
//
// A screen scoped to Organization / Location / Device-group needs that level's id to be
// resolved before it can load data. When the id is missing (empty string, null, or the
// literal strings 'undefined' / 'null'), the scoped API is called with an empty/invalid
// scope and the backend returns 52013 ("Invalid scope code") -> "Failed to load data".
//
// resolveScopeFallback() maps "this level's id is missing" to the nearest resolved
// ancestor route, cascading up to the always-valid MSP home.

// Treat null / '' / 'undefined' / 'null' all as "no id".
export const isBlank = (v) => v == null || v === '' || v === 'undefined' || v === 'null';

export const MSP_HOME = '/mspHome/dashboard';

// Map a pathname to the scope level it requires. Conservative: anything not clearly
// organization / location / device-group scoped returns '0' (no guard), so MSP home,
// onboarding, profile, Wi-Fi, device pages etc. are never touched.
export function scopeForPath(pathname = '') {
  if (pathname.startsWith('/organization/')) return '1';
  if (pathname === '/dashboard' || pathname.startsWith('/dashboard/') || pathname.startsWith('/locationSettings')) return '2';
  if (pathname.startsWith('/device-group')) return '3';
  return '0';
}

// Pull the org-name segment out of an /organization/:orgName/... path (Option D input).
export function orgNameFromPath(pathname = '') {
  const m = /^\/organization\/([^/]+)/.exec(pathname);
  return m ? decodeURIComponent(m[1]) : undefined;
}

/**
 * @param {object} p
 * @param {string} p.requiredScope  the scope the wrapped route needs: '1' org | '2' loc | '3' device group
 * @param {string} [p.orgId]
 * @param {string} [p.orgName]      org name segment (for building the org route)
 * @param {string} [p.currentNetwork]
 * @param {string} [p.currentDeviceGrpId]
 * @returns {string|null} path to redirect to (nearest resolved ancestor), or null to stay
 */
export function resolveScopeFallback({ requiredScope, orgId, orgName, currentNetwork, currentDeviceGrpId }) {
  const hasOrg = !isBlank(orgId) && !isBlank(orgName);
  const hasLoc = !isBlank(currentNetwork);
  const hasDeviceGroup = !isBlank(currentDeviceGrpId);

  switch (String(requiredScope)) {
    case '3': // device group
      if (hasDeviceGroup) return null;
      if (hasLoc) return '/dashboard';
      if (hasOrg) return `/organization/${orgName}/dashboard`;
      return MSP_HOME;
    case '2': // location
      if (hasLoc) return null;
      if (hasOrg) return `/organization/${orgName}/dashboard`;
      return MSP_HOME;
    case '1': // organization
      if (hasOrg) return null;
      return MSP_HOME;
    case '0': // MSP is the top of the tree — never redirect
    default:
      return null;
  }
}
